<template>
  <div class="fi-urlshort" style="margin-top:20px">

    <FiPageRail title="URL Shortener" />

    <div v-if="loading" class="d-flex align-items-center justify-content-center" style="min-height: 200px;">
      <b-spinner variant="primary"></b-spinner>
    </div>

    <div v-else style="text-align: left; color: #666;" class="d-flex flex-column align-items-center">

      <!-- Stats Cards -->
      <div style="width: 90%; display: flex; gap: 20px; margin-bottom: 25px;">
        <div style="flex: 1; background: #f8f9fa; border: 1px solid #dee2e6; border-radius: 8px; padding: 20px; text-align: center;">
          <div style="font-size: 32px; font-weight: bold; color: var(--fi-brand);">{{ stats.total_urls || 0 }}</div>
          <div style="font-size: 14px; color: #888;">Total URLs</div>
        </div>
        <div style="flex: 1; background: #f8f9fa; border: 1px solid #dee2e6; border-radius: 8px; padding: 20px; text-align: center;">
          <div style="font-size: 32px; font-weight: bold; color: var(--fi-brand);">{{ stats.total_clicks || 0 }}</div>
          <div style="font-size: 14px; color: #888;">Total Clicks</div>
        </div>
      </div>

      <!-- Create Form -->
      <div style="width: 90%; background: #f8f9fa; border: 1px solid #dee2e6; border-radius: 8px; padding: 15px; margin-bottom: 25px;">
        <span style="color: var(--fi-rail-accent); font-weight: bold; font-size: 18px;">Create Short URL</span>
        <div style="display: flex; gap: 10px; margin-top: 10px;">
          <b-form-input
            v-model="new_url"
            placeholder="Enter URL to shorten..."
            size="sm"
            style="flex: 3;"
          ></b-form-input>
          <b-form-input
            v-model="new_tenant_id"
            placeholder="Tenant ID (optional)"
            size="sm"
            style="flex: 1;"
          ></b-form-input>
          <b-button size="sm" variant="primary" @click="create_url" :disabled="!new_url || creating">
            <span v-if="creating"><b-spinner small></b-spinner></span>
            <span v-else>Create</span>
          </b-button>
        </div>
        <div v-if="created_url" style="margin-top: 10px; padding: 8px; background: #d4edda; border-radius: 4px; font-size: 14px;">
          Created: <strong>{{ created_url }}</strong>
          <b-button size="sm" variant="outline-success" class="ml-2" @click="copy_to_clipboard(created_url)">Copy</b-button>
        </div>
      </div>

      <!-- Filters -->
      <div style="width: 90%; margin-bottom: 15px;">
        <span style="color: var(--fi-rail-accent); font-weight: bold; font-size: 20px;">URLs</span>
        <hr style="margin: 5px 0 15px 0;"/>
        <div style="display: flex; gap: 10px; align-items: center;">
          <b-form-input v-model="filter_search" placeholder="Search destination URL..." size="sm" style="flex: 2;" @input="debounced_load"></b-form-input>
          <b-form-input v-model="filter_tenant" placeholder="Filter by tenant ID" size="sm" style="flex: 1;" @change="load_urls"></b-form-input>
          <b-form-input v-model="filter_start_date" type="date" size="sm" style="flex: 1;" @change="load_urls"></b-form-input>
          <b-form-input v-model="filter_end_date" type="date" size="sm" style="flex: 1;" @change="load_urls"></b-form-input>
          <b-button size="sm" variant="outline-secondary" @click="clear_filters">Clear</b-button>
        </div>
      </div>

      <!-- URLs Table -->
      <div style="width: 90%;">
        <table class="table table-sm table-hover" style="font-size: 14px;">
          <thead style="background: #f8f9fa;">
            <tr>
              <th class="sortable-th" @click="set_sort('short_code')">Short URL <span v-if="sort_key === 'short_code'">{{ sort_dir === 'asc' ? '▲' : '▼' }}</span></th>
              <th class="sortable-th" @click="set_sort('long_url')">Destination <span v-if="sort_key === 'long_url'">{{ sort_dir === 'asc' ? '▲' : '▼' }}</span></th>
              <th class="sortable-th" @click="set_sort('tenant_id')">Tenant <span v-if="sort_key === 'tenant_id'">{{ sort_dir === 'asc' ? '▲' : '▼' }}</span></th>
              <th class="sortable-th" @click="set_sort('source_service')">Source <span v-if="sort_key === 'source_service'">{{ sort_dir === 'asc' ? '▲' : '▼' }}</span></th>
              <th class="sortable-th" @click="set_sort('created_at')">Created <span v-if="sort_key === 'created_at'">{{ sort_dir === 'asc' ? '▲' : '▼' }}</span></th>
              <th class="sortable-th" style="text-align: center;" @click="set_sort('click_count')">Clicks <span v-if="sort_key === 'click_count'">{{ sort_dir === 'asc' ? '▲' : '▼' }}</span></th>
              <th style="text-align: center;">Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="url in filtered_urls" :key="url.short_code">
              <td style="white-space: nowrap;">
                <code style="font-size: 12px;">{{ short_domain }}/{{ url.short_code }}</code>
                <b-button size="sm" variant="link" class="p-0 ml-1" @click="copy_to_clipboard(short_domain + '/' + url.short_code)" title="Copy">
                  <span style="font-size: 12px;">📋</span>
                </b-button>
              </td>
              <td style="max-width: 300px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;" :title="url.long_url">
                {{ url.long_url }}
              </td>
              <td>{{ url.tenant_id }}</td>
              <td>{{ url.source_service }}</td>
              <td style="white-space: nowrap;">{{ format_date(url.created_at) }}</td>
              <td style="text-align: center;">
                <b-button size="sm" variant="link" class="p-0" @click="show_clicks(url.short_code)">
                  {{ url.click_count || 0 }}
                </b-button>
              </td>
              <td style="text-align: center;">
                <b-button size="sm" variant="outline-danger" @click="confirm_delete(url.short_code)">Delete</b-button>
              </td>
            </tr>
            <tr v-if="filtered_urls.length === 0">
              <td colspan="7" style="text-align: center; color: #999; padding: 30px;">No URLs found</td>
            </tr>
          </tbody>
        </table>

        <div v-if="last_key" style="text-align: center; margin-bottom: 20px;">
          <b-button size="sm" variant="outline-primary" @click="load_more" :disabled="loading_more">
            <span v-if="loading_more"><b-spinner small></b-spinner> Loading...</span>
            <span v-else>Load More</span>
          </b-button>
        </div>
      </div>
    </div>

    <!-- Click History Modal -->
    <b-modal v-model="show_click_modal" :title="'Click History — ' + click_short_code" size="lg" ok-only ok-title="Close">
      <div v-if="clicks_loading" class="text-center p-3"><b-spinner></b-spinner></div>
      <div v-else>
        <table class="table table-sm" style="font-size: 13px;" v-if="clicks.length > 0">
          <thead>
            <tr>
              <th>Timestamp</th>
              <th>Referrer</th>
              <th>User Agent</th>
              <th>IP</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(click, idx) in clicks" :key="idx">
              <td style="white-space: nowrap;">{{ format_datetime(click.clicked_at) }}</td>
              <td style="max-width: 200px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;" :title="click.referrer">{{ click.referrer || '—' }}</td>
              <td style="max-width: 200px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;" :title="click.user_agent">{{ click.user_agent || '—' }}</td>
              <td>{{ click.ip_address || '—' }}</td>
            </tr>
          </tbody>
        </table>
        <div v-else style="text-align: center; color: #999; padding: 20px;">No clicks recorded</div>
        <div v-if="clicks_last_key" style="text-align: center;">
          <b-button size="sm" variant="outline-primary" @click="load_more_clicks">Load More</b-button>
        </div>
      </div>
    </b-modal>

    <!-- Delete Confirmation -->
    <b-modal v-model="show_delete_modal" title="Delete Short URL" @ok="delete_url">
      <p>Are you sure you want to delete <strong>{{ delete_short_code }}</strong> and all its click history?</p>
    </b-modal>

  </div>
</template>

<script>
import { get_url_shortener_stats, get_url_shortener_urls, create_url_shortener_url, delete_url_shortener_url, get_url_shortener_clicks } from '@/networking/apis'

export default {
  name: 'url_shortener',
  data() {
    return {
      loading: true,
      stats: {},
      urls: [],
      last_key: null,
      loading_more: false,
      new_url: '',
      new_tenant_id: '',
      creating: false,
      created_url: '',
      filter_search: '',
      filter_tenant: '',
      filter_start_date: '',
      filter_end_date: '',
      show_click_modal: false,
      click_short_code: '',
      clicks: [],
      clicks_last_key: null,
      clicks_loading: false,
      show_delete_modal: false,
      delete_short_code: '',
      short_domain: '',
      debounce_timer: null,
      sort_key: 'created_at',
      sort_dir: 'desc',
    }
  },
  computed: {
    filtered_urls() {
      let list = this.urls
      if (this.filter_search) {
        const q = this.filter_search.toLowerCase()
        list = list.filter(u => u.long_url && u.long_url.toLowerCase().includes(q))
      }
      if (!this.sort_key) return list
      const key = this.sort_key
      const dir = this.sort_dir === 'asc' ? 1 : -1
      return [...list].sort((a, b) => {
        let va = a[key], vb = b[key]
        if (key === 'click_count') {
          va = Number(va) || 0
          vb = Number(vb) || 0
          return (va - vb) * dir
        }
        va = (va || '').toString().toLowerCase()
        vb = (vb || '').toString().toLowerCase()
        if (va < vb) return -1 * dir
        if (va > vb) return 1 * dir
        return 0
      })
    }
  },
  async mounted() {
    const api_url = process.env.VUE_APP_API_URL || ''
    if (api_url.includes('localhost')) {
      this.short_domain = api_url.replace(/\/$/, '')
    } else if (api_url.includes('dev-')) {
      this.short_domain = 'https://dev-api.fleet-integrations.com/bookings'
    } else {
      this.short_domain = 'https://fleet-int.com'
    }
    await this.load_all()
  },
  methods: {
    async load_all() {
      this.loading = true
      try {
        await Promise.all([this.load_stats(), this.load_urls()])
      } finally {
        this.loading = false
      }
    },
    async load_stats() {
      try {
        const resp = await get_url_shortener_stats(this.filter_tenant || '')
        if (resp.value && resp.value.data) {
          this.stats = resp.value.data
        }
      } catch (e) {
        console.error('Failed to load stats', e)
      }
    },
    async load_urls() {
      try {
        const resp = await get_url_shortener_urls(this.filter_tenant, this.filter_start_date, this.filter_end_date, 50, null)
        if (resp.value && resp.value.data) {
          this.urls = resp.value.data.items || []
          this.last_key = resp.value.data.last_key || null
        }
      } catch (e) {
        console.error('Failed to load urls', e)
      }
    },
    async load_more() {
      if (!this.last_key) return
      this.loading_more = true
      try {
        const resp = await get_url_shortener_urls(this.filter_tenant, this.filter_start_date, this.filter_end_date, 50, JSON.stringify(this.last_key))
        if (resp.value && resp.value.data) {
          this.urls = this.urls.concat(resp.value.data.items || [])
          this.last_key = resp.value.data.last_key || null
        }
      } finally {
        this.loading_more = false
      }
    },
    async create_url() {
      if (!this.new_url) return
      this.creating = true
      this.created_url = ''
      try {
        const resp = await create_url_shortener_url(this.new_url, this.new_tenant_id || 'manual')
        if (resp.value && resp.value.data) {
          this.created_url = resp.value.data.short_url
          this.new_url = ''
          this.new_tenant_id = ''
          await Promise.all([this.load_stats(), this.load_urls()])
        }
      } catch (e) {
        console.error('Failed to create url', e)
        alert('Failed to create short URL')
      } finally {
        this.creating = false
      }
    },
    async show_clicks(short_code) {
      this.click_short_code = short_code
      this.clicks = []
      this.clicks_last_key = null
      this.show_click_modal = true
      this.clicks_loading = true
      try {
        const resp = await get_url_shortener_clicks(short_code, 50, null)
        if (resp.value && resp.value.data) {
          this.clicks = resp.value.data.items || []
          this.clicks_last_key = resp.value.data.last_key || null
        }
      } finally {
        this.clicks_loading = false
      }
    },
    async load_more_clicks() {
      if (!this.clicks_last_key) return
      try {
        const resp = await get_url_shortener_clicks(this.click_short_code, 50, JSON.stringify(this.clicks_last_key))
        if (resp.value && resp.value.data) {
          this.clicks = this.clicks.concat(resp.value.data.items || [])
          this.clicks_last_key = resp.value.data.last_key || null
        }
      } catch (e) {
        console.error('Failed to load more clicks', e)
      }
    },
    confirm_delete(short_code) {
      this.delete_short_code = short_code
      this.show_delete_modal = true
    },
    async delete_url() {
      try {
        await delete_url_shortener_url(this.delete_short_code)
        await Promise.all([this.load_stats(), this.load_urls()])
      } catch (e) {
        console.error('Failed to delete', e)
        alert('Failed to delete short URL')
      }
    },
    copy_to_clipboard(text) {
      navigator.clipboard.writeText(text).catch(() => {
        const el = document.createElement('textarea')
        el.value = text
        document.body.appendChild(el)
        el.select()
        document.execCommand('copy')
        document.body.removeChild(el)
      })
    },
    clear_filters() {
      this.filter_search = ''
      this.filter_tenant = ''
      this.filter_start_date = ''
      this.filter_end_date = ''
      this.load_urls()
    },
    set_sort(key) {
      if (this.sort_key === key) {
        this.sort_dir = this.sort_dir === 'asc' ? 'desc' : 'asc'
      } else {
        this.sort_key = key
        this.sort_dir = key === 'click_count' ? 'desc' : 'asc'
      }
    },
    debounced_load() {
      clearTimeout(this.debounce_timer)
      this.debounce_timer = setTimeout(() => {}, 300)
    },
    format_date(iso) {
      if (!iso) return ''
      return new Date(iso).toLocaleDateString()
    },
    format_datetime(iso) {
      if (!iso) return ''
      return new Date(iso).toLocaleString()
    },
  }
}
</script>

<style scoped>
.sortable-th {
  cursor: pointer;
  user-select: none;
}
.sortable-th:hover {
  color: var(--fi-brand);
}
</style>
